import React, { FC, useState, useEffect, useRef } from 'react'
import { useAlert } from 'react-alert'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../../states/containers/store'
import {
  fetchOccupation,
} from '../../../../states/containers/member/memberSlice'

import {
  required,
  maxLength,
  email,
  password,
} from '../../../../utils/validate'
import { useTranslation } from 'react-i18next'
import { replaceAndStrip } from '../../../../utils/string'
import Modal from '../../common/Modal'
import { secToHourMinute } from '../../../../utils/date'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { IconName } from '@fortawesome/fontawesome-common-types'
import AddTeamModule from './AddTeamModule'
import * as AutoKana from 'vanilla-autokana'
import { RequestPatchSettingMember, RequestPostSettingMember, RoleList, RoleTeam, SettingMember } from '../../../../states/types/setting'
import { createSettingMember, updateSettingMember } from '../../../../states/containers/setting/systemSlice'

interface Occupation {
  id: number
  name: string
}

interface ProfileDetail {
  name: string
  code: string | null
  kana: string | null
  email: string
  password: string
  password_confirm: string
  occupation_id: number | null
  work_style_ids: number[]
  base_work_time: number
  base_active_time: number
  base_inactive_time: number
  contract: string
  place: string
  manager_teams: { id: number, name: string }[]
  member_teams: { id: number, name: string }[]
  send_mail: boolean
  holiday_group_id: number
}

interface MemberModalState {
  display: boolean,
  type: string,
  id: number | undefined,
  targetMemberData: SettingMember | null
}

interface Errors {
  name: string[],
  email: string[],
  password: string[],
  password_confirm: string[],
  occupation: string[],
  work_style: string[],
  base_active_time: string[],
  base_inactive_time: string[],
}

interface Props {
  memberModalState: MemberModalState
  closeCallback: VoidFunction
  addCallback?: VoidFunction
  modCallback?: VoidFunction
  closeable?: boolean
  teamList: RoleTeam[]
  roleList: RoleList[]
}

const MemberModal: FC<Props> = (props) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const alert = useAlert()

  const {
    commonPart,
    login,
    profile,
    memberList,
    holidaySchedule,
  } = useSelector((state: RootState) => state)

  const {
    memberModalState,
    closeCallback,
    addCallback,
    modCallback,
    closeable,
    teamList,
    roleList,
  } = props

  const initialInput = {
    name: "",
    code: null,
    kana: null,
    email: "",
    password: "",
    password_confirm: "",
    occupation_id: null,
    work_style_ids: [],
    base_work_time: 0,
    base_active_time: 0,
    base_inactive_time: 0,
    contract: "",
    place: "",
    manager_teams: [],
    member_teams: [],
    send_mail: true,
    holiday_group_id: 0,
  }

  const initErrors = {
    name: [],
    email: [],
    password: [],
    password_confirm: [],
    occupation: [],
    work_style: [],
    base_active_time: [],
    base_inactive_time: [],
  }

  const [profileData, setProfileData] = useState<ProfileDetail>(initialInput)

  const [openPassword, setOpenPassword] = useState(false)

  const [displayModal, setDisplayModal] = useState<boolean>(false)
  const [errors, setErrors] = useState<Errors>(initErrors)

  const [isSubmit, setIsSubmit] = useState<boolean>(false)

  const [selectRoleId, setSelectRoleId] = useState<number>(0)

  const kanaRef = useRef() as React.MutableRefObject<HTMLInputElement>

  // name
  const updateName = (event: React.ChangeEvent<HTMLInputElement>) => {
    setProfileData({
      ...profileData,
      name: replaceAndStrip(event.target.value),
    })
    if (event.target.value) {
      setErrors({
        ...errors,
        name: [],
      })
    }
  }

  const updateNameTmp = (event: React.ChangeEvent<HTMLInputElement>) => {
    setProfileData({
      ...profileData,
      name: event.target.value,
    })
  }

  // code
  const updateCode = (event: React.ChangeEvent<HTMLInputElement>) => {
    setProfileData({
      ...profileData,
      code: replaceAndStrip(event.target.value),
    })
  }

  const updateCodeTmp = (event: React.ChangeEvent<HTMLInputElement>) => {
    setProfileData({
      ...profileData,
      code: event.target.value,
    })
  }

  // email
  const updateEmail = (event: React.ChangeEvent<HTMLInputElement>) => {
    setProfileData({
      ...profileData,
      email: replaceAndStrip(event.target.value),
    })
    if (event.target.value) {
      setErrors({
        ...errors,
        email: [],
      })
    }
  }

  // password
  const updatePassword = (event: React.ChangeEvent<HTMLInputElement>) => {
    setProfileData({
      ...profileData,
      password: replaceAndStrip(event.target.value),
    })
    if (event.target.value) {
      setErrors({
        ...errors,
        password: [],
      })
    }
  }

  const updatePasswordConfirmation = (event: React.ChangeEvent<HTMLInputElement>) => {
    setProfileData({
      ...profileData,
      password_confirm: replaceAndStrip(event.target.value),
    })
    if (event.target.value) {
      setErrors({
        ...errors,
        password_confirm: [],
      })
    }
  }

  // occupation
  const updateOccupation = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setProfileData({
      ...profileData,
      occupation_id: Number(event.target.value),
    })
    if (event.target.value) {
      setErrors({
        ...errors,
        occupation: [],
      })
    }
  }

  // work style
  const updateWorkStyle = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setProfileData({
      ...profileData,
      work_style_ids: [Number(event.target.value)],
    })
    if (event.target.value) {
      setErrors({
        ...errors,
        work_style: [],
      })
    }
  }

  // active time
  const updateBaseActiveTime = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setProfileData({
      ...profileData,
      base_active_time: Number(event.target.value),
      base_work_time: Number(event.target.value) + profileData.base_inactive_time,
    })
    if (event.target.value) {
      setErrors({
        ...errors,
        work_style: [],
      })
    }
  }

  // inactive time
  const updateBaseInactiveTime = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setProfileData({
      ...profileData,
      base_inactive_time: Number(event.target.value),
      base_work_time: Number(event.target.value) + profileData.base_active_time,
    })
    if (event.target.value) {
      setErrors({
        ...errors,
        work_style: [],
      })
    }
  }

  // holiday group
  const updateHolidayGroup = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setProfileData({
      ...profileData,
      holiday_group_id: Number(event.target.value),
    })
  }

  // contract
  const updateContract = (event: React.ChangeEvent<HTMLInputElement>) => {
    setProfileData({
      ...profileData,
      contract: replaceAndStrip(event.target.value),
    })
  }

  const updateContractTmp = (event: React.ChangeEvent<HTMLInputElement>) => {
    setProfileData({
      ...profileData,
      contract: event.target.value,
    })
  }

  // place
  const updatePlace = (event: React.ChangeEvent<HTMLInputElement>) => {
    setProfileData({
      ...profileData,
      place: replaceAndStrip(event.target.value),
    })
  }

  const updatePlaceTmp = (event: React.ChangeEvent<HTMLInputElement>) => {
    setProfileData({
      ...profileData,
      place: event.target.value,
    })
  }

  const togglePasswordForm = () => {
    if (openPassword) {
      setOpenPassword(false)
      setProfileData({
        ...profileData,
        password: initialInput.password,
        password_confirm: initialInput.password_confirm,
      })
    } else {
      setOpenPassword(true)
    }
  }

  // manager_teams
  const updateManagerTeams = (team: { id: number, name: string }[]) => {
    setProfileData({
      ...profileData,
      manager_teams: team,
    })
  }

  // manager_teams
  const updateMemberTeams = (team: { id: number, name: string }[]) => {
    setProfileData({
      ...profileData,
      member_teams: team,
    })
  }

  const toggleSendMail = () => {
    setProfileData({
      ...profileData,
      send_mail: !profileData.send_mail,
    })
  }

  const saveMember = () => {
    if (!commonPart.company?.id) return
    setIsSubmit(true)
    const tmpErrors: Errors = initErrors
    let errorFlg = false
    // name
    const nameEmpty = required('name')(profileData.name)
    if (nameEmpty) {
      tmpErrors.name.push(t(nameEmpty, { label: t('model.member.name') }))
      errorFlg = true
    }
    const nameLength = maxLength(30)(profileData.name)
    if (nameLength) {
      tmpErrors.name.push(t(nameLength, { length: 30 }))
      errorFlg = true
    }
    // email
    const emailEmpty = required('email')(profileData.email)
    if (emailEmpty) {
      tmpErrors.email.push(t(emailEmpty, { label: t('model.member.email') }))
      errorFlg = true
    }
    const emailError = email(profileData.email)
    if (emailError) {
      tmpErrors.email.push(t(emailError))
      errorFlg = true
    }
    // email duplicate
    if (memberModalState.type === "create" && memberList.members.filter((v) => v.email === profileData.email).length) {
      tmpErrors.email.push(t('validate.email_duplicate'))
      errorFlg = true
    }
    if (openPassword) {
      // password
      const passwordError = password(profileData.password, { email: profileData.email })
      if (passwordError) {
        tmpErrors.password = passwordError.map((error) => t(error))
        errorFlg = true
      }
      // password_confirmation
      if (profileData.password_confirm !== profileData.password) {
        tmpErrors.password_confirm.push(t('form.error.confirmation_not_match'))
        errorFlg = true
      }
    }
    setErrors({
      name: tmpErrors.name,
      email: tmpErrors.email,
      password: tmpErrors.password,
      password_confirm: tmpErrors.password_confirm,
      occupation: tmpErrors.occupation,
      work_style: tmpErrors.work_style,
      base_active_time: tmpErrors.base_active_time,
      base_inactive_time: tmpErrors.base_inactive_time,
    })
    if (errorFlg) {
      setIsSubmit(false)
      return
    }

    if (memberModalState.type === "create") {
      const params: RequestPostSettingMember = {
        name: profileData.name,
        kana: replaceAndStrip(kanaRef.current.value) || null,
        email: profileData.email,
        code: profileData.code,
        contract: profileData.contract,
        place: profileData.place,
        occupation_id: profileData.occupation_id,
        send_mail: profileData.send_mail,
        holiday_group_id: profileData.holiday_group_id,
      }
      if (login.role_permission?.team_setting) {
        params.teams = {
          member_team_ids: profileData.member_teams.map((v) => v.id),
          manager_team_ids: profileData.manager_teams.map((v) => v.id),
        }
      }
      if (login.role_permission?.role_setting) {
        params.role_id = selectRoleId
      }
      if (openPassword) {
        params.password = profileData.password
        params.password_confirm = profileData.password_confirm
      }
      dispatch(
        createSettingMember(
          params,
          () => {
            alert.success(
              <>
                {t('setting.member.alert.register_info', { name: profileData.name })}
                {profileData.send_mail && t('setting.member.alert.register_info_send_mail', { name: profileData.name })}
                <br />
                {t('setting.member.alert.register_info2')}
              </>,
            )
            if (addCallback) addCallback()
            closeCallback()
          },
          () => {
            alert.error('エラーが発生しました。管理者にお問い合わせください。')
            setIsSubmit(false)
          },
        ),
      )
    } else {
      const { targetMemberData } = memberModalState
      if (!targetMemberData) return
      const params: RequestPatchSettingMember = {}
      if (profileData.name !== targetMemberData.name) {
        params.name = profileData.name
      }
      if (kanaRef.current.value !== targetMemberData.kana) {
        params.kana = replaceAndStrip(kanaRef.current.value)
      }
      if (profileData.email !== targetMemberData.email) {
        params.email = profileData.email
      }
      if (profileData.code !== targetMemberData.code) {
        params.code = profileData.code
      }
      if (openPassword && profileData.password) {
        params.password = profileData.password
        params.password_confirm = profileData.password_confirm
      }
      if (login.role_permission?.team_setting) {
        params.teams = {
          member_team_ids: profileData.member_teams.map((v) => v.id),
          manager_team_ids: profileData.manager_teams.map((v) => v.id),
        }
      }
      if (login.role_permission?.role_setting) {
        params.role_id = selectRoleId
      }
      dispatch(
        updateSettingMember(
          targetMemberData.id,
          params,
          () => {
            alert.success(t('setting.member.alert.update_info', { name: profileData.name }))
            if (modCallback) modCallback()
            closeCallback()
          },
          () => {
            alert.error('エラーが発生しました。管理者にお問い合わせください。')
            setIsSubmit(false)
          },
        ),
      )
    }
  }

  useEffect(() => {
    if (!memberModalState || !roleList.length) return
    const { targetMemberData } = memberModalState
    if (memberModalState.type === "update" && targetMemberData?.name) {
      setProfileData({
        ...profileData,
        name: targetMemberData.name,
        kana: targetMemberData.kana || null,
        code: targetMemberData.code || null,
        email: targetMemberData.email,
        manager_teams: targetMemberData.manager_teams,
        member_teams: targetMemberData.member_teams,
        password: initialInput.password,
        password_confirm: initialInput.password_confirm,
      })
      setSelectRoleId(roleList.find((v) => v.name === targetMemberData.role_name)?.id || 0)
    } else {
      setProfileData({
        ...profileData,
        name: initialInput.name,
        kana: initialInput.kana,
        code: initialInput.code,
        email: initialInput.email,
        manager_teams: initialInput.manager_teams,
        member_teams: initialInput.member_teams,
        password: initialInput.password,
        password_confirm: initialInput.password_confirm,
      })
      setSelectRoleId(roleList.find((v) => v.name === "一般ユーザー")?.id || 0)
    }
    setOpenPassword(false)
    setErrors(initErrors)
  }, [memberModalState, roleList])

  useEffect(() => {
    setDisplayModal(memberModalState.display)
    setIsSubmit(false)
  }, [memberModalState.display])

  useEffect(() => {
    if (!login.accessToken) return
    dispatch(fetchOccupation())
  }, [login.accessToken])

  useEffect(() => {
    if (!displayModal) return
    AutoKana.bind('#name', '#kana')
  }, [displayModal])

  return (
    <Modal
      title={memberModalState.type === "update" ? t('setting.member_modal.modify_member.title') : t('setting.member_modal.new_member.title')}
      show={displayModal}
      wrapperClass="setting_member"
      closeCallback={closeCallback}
      closeable={closeable}
    >
      <>
        <fieldset>
          <legend>
            基本情報
          </legend>
          <div className="item">
            <div className="title flex">
              <div className="label red">必須</div>
              {t('model.member.name')}
            </div>
            <div className="detail row_1">
              <input
                id="name"
                type="text"
                placeholder={t('model.member.name')}
                onChange={(event) => updateNameTmp(event)}
                onBlur={(event) => updateName(event)}
                value={profileData.name}
              />
            </div>
            {
              errors.name && errors.name.map((error) => (
                <div className="error" key={error}>
                  {error}
                </div>
              ))
            }
          </div>
          <div className="item">
            <div className="title flex">
              <div className="label green">任意</div>
              かな
            </div>
            <div className="detail row_1">
              <input
                ref={kanaRef}
                id="kana"
                type="text"
                placeholder="よみがな"
                defaultValue={profileData.kana || ""}
              />
            </div>
          </div>
          <div className="item">
            <div className="title flex">
              <div className="label green">任意</div>
              社員コード
            </div>
            <div className="detail row_1">
              <input
                type="text"
                placeholder="社員番号など"
                onChange={(event) => updateCodeTmp(event)}
                onBlur={(event) => updateCode(event)}
                value={profileData.code || ""}
              />
            </div>
          </div>
          <div className="item">
            <div className="title flex">
              <div className="label red">必須</div>
              {t('model.member.email')}
            </div>
            <div className="detail row_1">
              <input
                type="text"
                placeholder={t('model.member.email')}
                onChange={(event) => updateEmail(event)}
                value={profileData.email}
              />
            </div>
            {
              errors.email && errors.email.map((error) => (
                <div className="error" key={error}>
                  {error}
                </div>
              ))
            }
          </div>
        </fieldset>
        {
          login.role_permission?.team_setting && (
            <fieldset>
              <legend>
                チーム
              </legend>
              <div className="item">
                <div className="title flex">
                  <div className="label green">任意</div>
                  マネージャーで参加しているチーム
                </div>
                <div className="detail equal_space row_1">
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      border: "solid 1px #CCCCCC",
                      borderRadius: "4px",
                      width: "100%",
                      padding: "8px",
                      flexFlow: "wrap",
                    }}
                  >
                    {
                      profileData.manager_teams.map((value) => (
                        <div
                          style={{
                            backgroundColor: "#FFFFFF",
                            border: "solid 1px #CCCCCC",
                            borderRadius: "20px",
                            padding: "4px 8px",
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                          key={`condition_${value.id}`}
                        >
                          {value.name}
                          <span
                            style={{
                              cursor: "pointer",
                            }}
                            onClick={() => updateManagerTeams([...profileData.manager_teams.filter((v) => v.id !== value.id)])}
                            role="none"
                          >
                            <FontAwesomeIcon icon={['fas', 'times']} />
                          </span>
                        </div>
                      ))
                    }
                    <AddTeamModule
                      teamIdList={profileData.manager_teams.map((v) => v.id)}
                      callbackTeams={(teams) => updateManagerTeams(teams)}
                      teamList={teamList.map((v) => ({ id: v.id, name: v.name }))}
                    />
                  </div>
                </div>
              </div>
              <div className="item">
                <div className="title flex">
                  <div className="label green">任意</div>
                  メンバーで参加しているチーム
                </div>
                <div className="detail equal_space row_1">
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      border: "solid 1px #CCCCCC",
                      borderRadius: "4px",
                      width: "100%",
                      padding: "8px",
                      flexFlow: "wrap",
                    }}
                  >
                    {
                      profileData.member_teams.map((value) => (
                        <div
                          style={{
                            backgroundColor: "#FFFFFF",
                            border: "solid 1px #CCCCCC",
                            borderRadius: "20px",
                            padding: "4px 8px",
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                          key={`condition_${value.id}`}
                        >
                          {value.name}
                          <span
                            style={{
                              cursor: "pointer",
                            }}
                            onClick={() => updateMemberTeams([...profileData.member_teams.filter((v) => v.id !== value.id)])}
                            role="none"
                          >
                            <FontAwesomeIcon icon={['fas', 'times']} />
                          </span>
                        </div>
                      ))
                    }
                    <AddTeamModule
                      teamIdList={profileData.member_teams.map((v) => v.id)}
                      callbackTeams={(teams) => updateMemberTeams(teams)}
                      teamList={teamList.map((v) => ({ id: v.id, name: v.name }))}
                    />
                  </div>
                </div>
              </div>
            </fieldset>
          )
        }
        {
          login.role_permission?.role_setting && (
            <fieldset>
              <legend>
                権限
              </legend>
              <div className="item">
                <div className="title flex">
                  <div className="label green">任意</div>
                  権限ロール
                </div>
                <div className="detail equal_space row_1" style={{ position: "relative" }}>
                  <select
                    onChange={(e) => setSelectRoleId(Number(e.target.value))}
                    value={selectRoleId}
                  >
                    {
                      roleList.map((v) => (
                        <option value={v.id}>
                          {v.name}
                        </option>
                      ))
                    }
                  </select>
                  <div
                    style={{
                      position: "absolute",
                      top: "50%",
                      right: "8px",
                      transform: "translateY(-50%)",
                      pointerEvents: "none",
                    }}
                  >
                    <FontAwesomeIcon icon={['fas', 'caret-down']} />
                  </div>
                </div>
                {
                  memberModalState.targetMemberData?.id === login.current_member_id && (
                    <div
                      style={{
                        border: "solid 1px #ff0000",
                        borderRadius: "4px",
                        padding: "8px",
                        backgroundColor: "#ffeaea",
                        color: "#FF0000",
                        marginTop: "8px",
                      }}
                    >
                      自身の権限ロールに関する操作をおこないます。ご注意ください。
                    </div>
                  )
                }
              </div>
            </fieldset>
          )
        }
        <fieldset>
          <legend>
            パスワード
          </legend>
          <div className="item">
            <div className="title">
              <div style={{ display: 'flex', gap: "8px", alignItems: "center" }}>
                {t('model.member.password')}
                <div
                  onClick={() => togglePasswordForm()}
                  role="none"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    cursor: "pointer",
                  }}
                >
                  <FontAwesomeIcon
                    icon={['far', `${!openPassword ? 'check-square' : 'square'}` as IconName]}
                    className={`icon ${!openPassword ? "checked" : "no_check"}`}
                    style={{
                      fontSize: "18px",
                      color: !openPassword ? "#08984C" : "#666666",
                    }}
                  />
                  <div>
                    <div className="check_box_title">
                      {
                        memberModalState.type === 'create' ? (
                          <>
                            {t('setting.member_modal.new_member.set_password_automatically')}
                          </>
                        ) : (
                          <>
                            パスワードを変更しない
                          </>
                        )
                      }
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {
              openPassword && (
                <>
                  <div className="detail row_1">
                    <input
                      type="text"
                      placeholder={t('model.member.password')}
                      onChange={(event) => updatePassword(event)}
                      value={profileData.password}
                    />
                  </div>
                  {
                    errors.password && errors.password.map((error) => (
                      <div className="error" key={error}>
                        {error}
                      </div>
                    ))
                  }
                </>
              )
            }
          </div>
          {
            openPassword && (
              <>
                <div className="item">
                  <div className="title">
                    {t('model.member.password_confirmation')}
                  </div>
                  <div className="detail row_1">
                    <input
                      type="text"
                      placeholder={t('model.member.password_confirmation')}
                      onChange={(event) => updatePasswordConfirmation(event)}
                      value={profileData.password_confirm}
                    />
                  </div>
                  {
                    errors.password_confirm && errors.password_confirm.map((error) => (
                      <div className="error" key={error}>
                        {error}
                      </div>
                    ))
                  }
                </div>
              </>
            )
          }
        </fieldset>
        {
          memberModalState.type === "create" && (
            <>
              <fieldset>
                <legend>
                  招待メール
                </legend>
                <div className="item">
                  <div className="title">
                    <div style={{ display: 'flex', gap: "8px", alignItems: "center" }}>
                      <div
                        onClick={() => toggleSendMail()}
                        role="none"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          cursor: "pointer",
                        }}
                      >
                        <FontAwesomeIcon
                          icon={['far', `${profileData.send_mail ? 'check-square' : 'square'}` as IconName]}
                          className={`icon ${profileData.send_mail ? "checked" : "no_check"}`}
                          style={{
                            fontSize: "18px",
                            color: profileData.send_mail ? "#08984C" : "#666666",
                          }}
                        />
                        <div>
                          <div className="check_box_title">
                            ユーザー作成後、招待メールを送信する
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </fieldset>
            </>
          )
        }
      </>
      <>
        <button type="button" className="button cancel" onClick={closeCallback} disabled={!closeable}>
          {t('setting.personal.custom_alert.modal.button.cancel')}
        </button>
        <button
          type="button"
          className="button"
          onClick={saveMember}
          disabled={isSubmit}
        >
          { memberModalState.type === 'update' ? "編集する" : "作成する" }
        </button>
      </>
    </Modal>
  )
}

MemberModal.defaultProps = {
  addCallback: undefined,
  modCallback: undefined,
  closeable: undefined,
}

export default MemberModal
